import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Check, ChevronLeft, ChevronRight, Wand2, Upload, RefreshCw,
  MapPin, Target as TargetIco, DollarSign, Rocket, AlertTriangle, Settings2, Image as ImageIcon,
  Megaphone, ClipboardList, Clock,
} from "lucide-react";
import { sr, MEDIA_CHANNELS } from "../../lib/sr";
import { auth } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const usd = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const CH_LABEL = Object.fromEntries(MEDIA_CHANNELS.map((c) => [c.key, c.label]));
const chKeyFromName = (n) => {
  const s = (n || "").toLowerCase();
  if (s.includes("chatgpt")) return "chatgpt";
  if (s.includes("radio")) return "radio";
  if (s.includes("search") || s.includes("google")) return "google";
  if (s.includes("meta") || s.includes("social") || s.includes("facebook") || s.includes("instagram")) return "social";
  if (s.includes("native")) return "native";
  if (s.includes("ctv") || s.includes("ott")) return "ctv";
  if (s.includes("video")) return "video";
  if (s.includes("audio")) return "audio";
  if (s.includes("display")) return "display";
  return null;
};
const isScriptCh = (k) => ["video", "ctv", "audio"].includes(k);

// Industry-appropriate primary goals. Keys are plain goal strings the plan engine understands.
const GOALS_BY_INDUSTRY = {
  restaurant: [["visits", "More diners & foot traffic"], ["orders", "More online orders"], ["bookings", "More reservations"], ["awareness", "Build local awareness"]],
  retail: [["visits", "More store visits"], ["sales", "More online sales"], ["awareness", "Build awareness"], ["leads", "Grow my list"]],
  services: [["leads", "More leads & enquiries"], ["calls", "More phone calls"], ["bookings", "More appointments"], ["awareness", "Build awareness"]],
  medical: [["bookings", "More appointments"], ["calls", "More phone calls"], ["leads", "More new-patient leads"], ["awareness", "Build awareness"]],
  realestate: [["leads", "More buyer/seller leads"], ["calls", "More phone calls"], ["awareness", "Build awareness"], ["bookings", "More viewings"]],
  default: [["visits", "More visits & foot traffic"], ["leads", "More leads"], ["sales", "More sales"], ["bookings", "More bookings"], ["calls", "More calls"], ["awareness", "Build awareness"]],
};
const goalsFor = (industry) => GOALS_BY_INDUSTRY[(industry || "").toLowerCase()] || GOALS_BY_INDUSTRY.default;

const DURATIONS = [["2 weeks", 14], ["1 month", 30], ["3 months", 90]];
const STEPS = [
  { n: 1, label: "Budget & Goal", icon: DollarSign },
  { n: 2, label: "Recommended Plan", icon: Sparkles },
  { n: 3, label: "Design", icon: Wand2 },
  { n: 4, label: "Review", icon: ClipboardList },
  { n: 5, label: "Launch", icon: Rocket },
];

function Stepper({ step, maxReached, onGo }) {
  return (
    <div data-testid="easy-stepper" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 22 }}>
      {STEPS.map((s, i) => {
        const done = s.n < step;
        const active = s.n === step;
        const reachable = s.n <= maxReached;
        const Icon = s.icon;
        return (
          <React.Fragment key={s.n}>
            <button type="button" onClick={() => reachable && onGo(s.n)} disabled={!reachable} data-testid={`easy-step-tab-${s.n}`}
              style={{ display: "flex", alignItems: "center", gap: 8, border: "none", background: "transparent", cursor: reachable ? "pointer" : "default", padding: 0 }}>
              <span style={{
                width: 30, height: 30, borderRadius: 999, display: "grid", placeItems: "center", flexShrink: 0,
                background: active ? "var(--navy)" : done ? "var(--success, #16a34a)" : "#EEF1F5",
                color: active || done ? "#fff" : "var(--muted)", fontWeight: 800, fontSize: 13,
              }}>{done ? <Check size={15} /> : <Icon size={15} />}</span>
              <span style={{ fontSize: 13, fontWeight: active ? 800 : 600, color: active ? "var(--navy)" : "var(--muted)", whiteSpace: "nowrap" }}>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <span style={{ flex: 1, minWidth: 16, height: 2, background: s.n < step ? "var(--success,#16a34a)" : "#E6EAF0" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function EasyCampaign() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [home, setHome] = useState(null);
  const [locations, setLocations] = useState([]);
  const [plan, setPlan] = useState(null);
  const [summary, setSummary] = useState(null);
  const [step, setStep] = useState(1);

  // Step 1 inputs
  const [locId, setLocId] = useState("");
  const [budget, setBudget] = useState(3000);
  const [durationDays, setDurationDays] = useState(30);
  const [goal, setGoal] = useState("visits");
  const [missingAnswer, setMissingAnswer] = useState("");

  // Step 2 adjust
  const [adjust, setAdjust] = useState("");
  // Step 3 design
  const [designMode, setDesignMode] = useState("ai");

  const industry = home?.workflow?.industry || home?.profile?.industry || home?.tenant?.industry || "";
  const bizName = summary?.business_name || home?.profile?.name || home?.tenant?.name || "your business";
  const hasTenant = !!home?.tenant?.id;

  const planChannelKeys = useMemo(() => {
    const p = plan?.plan || {};
    return [...new Set((plan?.channels?.length ? plan.channels : (p.channel_mix || []).map((c) => chKeyFromName(c.channel))).filter(Boolean))];
  }, [plan]);

  const deriveStep = (pl, sm) => {
    if (!pl) return 1;
    if (pl.status === "DRAFT") return 2;
    if (pl.status === "APPROVED" && !(sm?.launch?.creative_ready)) return 3;
    if (pl.status === "APPROVED") return 4;
    if (pl.status === "ACTIVATING" || pl.activated_at) return 5;
    return 2;
  };

  const loadSummary = async (id) => {
    const s = await sr.easySummary(id);
    setSummary(s); setPlan(s.growth_plan); return s;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [h, locs, plans] = await Promise.all([
        sr.home().catch(() => null), sr.locations().catch(() => ({ locations: [] })), sr.listPlans().catch(() => ({ growth_plans: [] })),
      ]);
      setHome(h); setLocations(locs.locations || []);
      const ib = Number(h?.tenant?.intended_monthly_budget) || null;
      if (ib) setBudget(ib);
      if ((locs.locations || []).length) setLocId(locs.locations[0].id);
      const list = plans.growth_plans || [];
      const pid = params.get("id");
      // Resume the most recent not-yet-live draft/approved plan, or the one requested.
      let p = (pid && list.find((x) => x.id === pid)) || list.find((x) => ["DRAFT", "APPROVED", "ACTIVATING"].includes(x.status)) || null;
      if (p?.id) {
        const s = await loadSummary(p.id);
        setBudget(s.growth_plan.monthly_budget || ib || 3000);
        setGoal(s.growth_plan.goal || "visits");
        const saved = Number(localStorage.getItem(`sr_easy_step_${p.id}`));
        setStep(saved && saved >= 1 && saved <= 5 ? Math.min(saved, deriveStep(s.growth_plan, s)) || deriveStep(s.growth_plan, s) : deriveStep(s.growth_plan, s));
      } else {
        setStep(1);
      }
    } catch { toast.error("Couldn't load Easy mode"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (plan?.id) try { localStorage.setItem(`sr_easy_step_${plan.id}`, String(step)); } catch { /* ignore */ } }, [step, plan]);

  const maxReached = plan ? Math.max(step, deriveStep(plan, summary)) : step;
  const goStep = (n) => setStep(n);

  // ---- Step 1: create/update the draft plan from budget + goal ----
  const startPlan = async () => {
    if (!hasTenant) return toast.error("Select a client first");
    if (!Number(budget) || Number(budget) < 1) return toast.error("Enter a monthly budget");
    setBusy("plan");
    try {
      let gp;
      const goalChanged = plan?.goal && goal && plan.goal !== goal;
      if (plan?.id && !goalChanged) {
        // Existing draft: update budget/goal IN PLACE — preserves audiences, geo, channel mix & creative.
        const r = await sr.easyUpdate(plan.id, { monthly_budget: Number(budget), goal });
        gp = r.growth_plan;
      } else if (plan?.id && goalChanged) {
        // Goal changed: re-recommend but carry over advanced work (revise preserves geo/creatives).
        const r = await sr.revisePlan(plan.id, { monthly_budget: Number(budget), goal });
        gp = r.growth_plan;
      } else {
        const brief = missingAnswer.trim() || undefined;
        const r = await sr.generatePlan({
          goal, monthly_budget: Number(budget), location_id: locId || locations[0]?.id,
          duration_days: durationDays, channels: MEDIA_CHANNELS.map((c) => c.key), brief,
        });
        gp = r.growth_plan;
      }
      const s = await loadSummary(gp.id);
      toast.success("Your recommended plan is ready");
      setStep(2); return s;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build a plan"); }
    finally { setBusy(""); }
  };

  // ---- Step 2: use / adjust ----
  const usePlan = async () => {
    setBusy("approve");
    try { await sr.approvePlan(plan.id); await loadSummary(plan.id); toast.success("Plan locked in — now add your creative"); setStep(3); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't lock the plan"); } finally { setBusy(""); }
  };
  // Proposal From Plan (Sales/admin only): reuse THIS saved plan as a client proposal. Never launches or charges.
  const me = auth.getUser();
  const canProposal = !!me && ["salesperson", "admin", "super_admin"].includes(me.role);
  const makeProposal = async () => {
    setBusy("proposal");
    try {
      const r = await sr.createProposal({ tenant_id: home?.tenant?.id, growth_plan_id: plan.id });
      toast.success("Proposal created — this doesn't launch the campaign or charge anything");
      nav(`/app/proposals/${r.proposal.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create proposal"); } finally { setBusy(""); }
  };
  const applyAdjust = async () => {
    setBusy("adjust");
    try {
      // Revise (not full regenerate) so audiences, geo & creative carry over.
      const r = await sr.revisePlan(plan.id, {
        goal, monthly_budget: Number(budget), brief: adjust.trim() || undefined,
      });
      await loadSummary(r.growth_plan.id); setAdjust(""); toast.success("Plan updated — your audiences & creative were kept");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't adjust"); } finally { setBusy(""); }
  };

  // ---- Step 3: design ----
  const aiForAll = async () => {
    setBusy("ai-all");
    try {
      let last = plan;
      for (const ch of planChannelKeys) {
        if ((plan?.creatives || {})[ch]) continue;
        try {
          const r = await sr.creativeVariantsFor(plan.id, ch, 1, 15);
          const c = (r.concepts || [])[0];
          if (!c) continue;
          const payload = (r.format === "script")
            ? { source: "ai", channel: ch, format: "script", title: c.title, script: c.scenes, music: c.music, cta: typeof c.cta === "string" ? c.cta : undefined, duration_sec: c.duration_sec || 15 }
            : { source: "ai", channel: ch, headline: c.headline, body: c.body, cta: c.cta };
          const res = await sr.addPlanCreative(plan.id, payload);
          last = res.growth_plan;
        } catch { /* keep going for other channels */ }
      }
      setPlan(last); await loadSummary(plan.id);
      toast.success("AI creative generated for your channels");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't generate creative"); } finally { setBusy(""); }
  };
  const regenChannel = async (ch) => {
    setBusy(`regen-${ch}`);
    try {
      const r = await sr.creativeVariantsFor(plan.id, ch, 1, 15);
      const c = (r.concepts || [])[0]; if (!c) throw new Error();
      const payload = (r.format === "script")
        ? { source: "ai", channel: ch, format: "script", title: c.title, script: c.scenes, music: c.music, cta: typeof c.cta === "string" ? c.cta : undefined, duration_sec: c.duration_sec || 15 }
        : { source: "ai", channel: ch, headline: c.headline, body: c.body, cta: c.cta };
      const res = await sr.addPlanCreative(plan.id, payload); setPlan(res.growth_plan); await loadSummary(plan.id);
    } catch (e) { toast.error("Couldn't regenerate"); } finally { setBusy(""); }
  };
  const uploadForChannel = async (ch, file) => {
    if (!file) return;
    setBusy(`upload-${ch}`);
    try {
      const up = await sr.uploadVideoPhoto(file);
      const url = up.url || up.photo_url || up.asset_url;
      const p = plan.plan || {};
      const res = await sr.addPlanCreative(plan.id, { source: "upload", channel: ch, image_url: url, headline: p.offer?.headline || plan.goal, body: p.offer?.detail || "", cta: "Learn more" });
      setPlan(res.growth_plan); await loadSummary(plan.id); toast.success(`Uploaded your design for ${CH_LABEL[ch] || ch}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed — check the file type/size"); } finally { setBusy(""); }
  };

  // ---- Step 5: launch ----
  const [spendApproved, setSpendApproved] = useState(false);
  const launch = async () => {
    if (!spendApproved) return toast.error("Please approve the monthly spend to continue");
    setBusy("launch");
    try {
      const r = await sr.activatePlan(plan.id);
      await loadSummary(plan.id);
      toast.success(summary?.launch?.self_serve ? "Submitted for review" : `${r.campaigns.length} campaign(s) launched`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit"); } finally { setBusy(""); }
  };

  if (loading) return <div className="sr-empty" style={{ padding: 60 }}><Loader2 className="sr-spin" size={26} /></div>;

  if (!hasTenant) {
    return (
      <div data-testid="easy-agency-gate" style={{ maxWidth: 560, margin: "40px auto" }}>
        <div className="sr-card sr-empty" style={{ textAlign: "center", padding: 40 }}>
          <MapPin size={28} color="var(--blue)" />
          <h2 style={{ fontSize: 22, marginTop: 10 }}>Pick a client to plan for</h2>
          <p className="sr-muted" style={{ marginTop: 8 }}>Easy mode builds a campaign for one business at a time. Use the client switcher at the top to choose the client you're growing, then come back here.</p>
        </div>
      </div>
    );
  }

  const p = plan?.plan || {};
  const pr = summary?.pricing || { media_budget: Number(budget), management_fee_pct: 15, management_fee: Math.round(Number(budget) * 0.15), total_customer_cost: Math.round(Number(budget) * 1.15) };

  return (
    <div data-testid="easy-campaign" style={{ maxWidth: 920, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <h1 style={{ fontSize: 28, margin: 0 }}>Build a campaign · Easy</h1>
          <p className="sr-muted" style={{ marginTop: 4 }}>Tell us your budget and goal, approve the plan, choose your creative and launch — for <strong>{bizName}</strong>.</p>
        </div>
        <Link to={`/app/growth-plan${plan?.id ? `?id=${plan.id}` : ""}`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="easy-open-pro"><Settings2 size={14} /> Pro mode</Link>
      </div>

      <Stepper step={step} maxReached={maxReached} onGo={goStep} />

      {/* STEP 1 — Budget & Goal */}
      {step === 1 && (
        <div data-testid="easy-step-1" style={{ display: "grid", gap: 16 }}>
          {locations.length > 1 && (
            <div className="sr-card" data-testid="easy-location-select">
              <label className="sr-label" style={{ display: "block", marginBottom: 8 }}>Which location is this campaign for?</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {locations.map((l) => {
                  const on = locId === l.id;
                  return (
                    <button key={l.id} type="button" onClick={() => setLocId(l.id)} data-testid={`easy-loc-${l.id}`}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{l.name}</button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="sr-card">
            <label className="sr-label">Monthly budget</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: "var(--muted)" }}>$</span>
              <input className="sr-input" type="number" min="1" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="easy-budget" style={{ maxWidth: 200, fontSize: 18 }} />
              <span className="sr-muted">/ month</span>
            </div>
            <div data-testid="easy-cost-preview" style={{ marginTop: 12, background: "var(--blue-light)", border: "1px solid var(--blue)", borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>Media budget (spent on ads)</span><strong data-testid="easy-media-budget">{usd(budget)}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}><span>Management fee (15%)</span><strong>{usd(Number(budget) * 0.15)}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--blue)" }}><span style={{ fontWeight: 800 }}>Total you pay</span><strong data-testid="easy-total-cost">{usd(Number(budget) * 1.15)}/mo</strong></div>
            </div>
          </div>

          <div className="sr-card">
            <label className="sr-label" style={{ display: "block", marginBottom: 8 }}>How long should it run?</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {DURATIONS.map(([lbl, d]) => {
                const on = durationDays === d;
                return <button key={d} type="button" onClick={() => setDurationDays(d)} data-testid={`easy-duration-${d}`}
                  style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{lbl}</button>;
              })}
            </div>
          </div>

          <div className="sr-card">
            <label className="sr-label" style={{ display: "block", marginBottom: 8 }}>What's the main result you want?</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 8 }}>
              {goalsFor(industry).map(([g, lbl]) => {
                const on = goal === g;
                return <button key={g} type="button" onClick={() => setGoal(g)} data-testid={`easy-goal-${g}`}
                  style={{ border: "1px solid " + (on ? "var(--navy)" : "var(--border)"), background: on ? "var(--navy)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 10, padding: "12px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                  <TargetIco size={15} style={{ marginRight: 8, verticalAlign: "-2px" }} />{lbl}</button>;
              })}
            </div>
          </div>

          {!industry && (
            <div className="sr-card" data-testid="easy-missing-context" style={{ borderLeft: "4px solid var(--gold,#E0A800)" }}>
              <label className="sr-label">Quick question — what kind of business is this?</label>
              <p className="sr-muted" style={{ fontSize: 12, margin: "2px 0 8px" }}>Helps the AI tailor audiences & channels. One line is plenty.</p>
              <input className="sr-input" value={missingAnswer} onChange={(e) => setMissingAnswer(e.target.value)} data-testid="easy-missing-input" placeholder="e.g. a family Italian restaurant in Plano" />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="sr-btn sr-btn-primary" onClick={startPlan} disabled={busy === "plan"} data-testid="easy-step1-continue">
              {busy === "plan" ? <><Loader2 size={15} className="sr-spin" /> Building your plan…</> : <>Build my plan <ChevronRight size={16} /></>}
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — Recommended Plan */}
      {step === 2 && plan && (
        <div data-testid="easy-step-2" style={{ display: "grid", gap: 14 }}>
          <div className="sr-card" style={{ borderLeft: "4px solid var(--blue)" }}>
            <div className="sr-eyebrow" style={{ marginBottom: 6 }}><Sparkles size={13} /> Recommended for you</div>
            <p style={{ fontSize: 15, lineHeight: 1.55 }}>{p.diagnosis || "A focused plan tuned to your budget and goal."}</p>
            {p.local_market && <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{p.local_market}</p>}
          </div>

          <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>What we'll promote</div>
              <strong style={{ color: "var(--navy)" }}>{p.offer?.headline || "Your business"}</strong>
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{p.offer?.detail}</p>
            </div>
            <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>Expected result <span className="sr-badge sr-badge-gray" style={{ marginLeft: 6 }}>estimate</span></div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span className="sr-stat-val">{p.forecast?.low}–{p.forecast?.high}</span><span className="sr-muted">{p.forecast?.unit}</span>
              </div>
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 4, textTransform: "capitalize" }}>{p.forecast?.metric}</div>
              {p.forecast?.assumptions?.length > 0 && <p className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>{p.forecast.assumptions.join(" · ")}</p>}
            </div>
          </div>

          <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 10 }}>Where your budget goes</div>
            {(p.channel_mix || []).map((c) => (
              <div key={c.channel} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}><strong style={{ color: "var(--navy)" }}>{c.channel}</strong><span>{c.budget_pct}%</span></div>
                <div className="sr-progress" style={{ margin: "4px 0" }}><div style={{ width: `${c.budget_pct}%` }} /></div>
                <div className="sr-muted" style={{ fontSize: 12 }}>{c.why}</div>
              </div>
            ))}
          </div>

          <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}><TargetIco size={13} /> Who we'll reach</div><p style={{ fontSize: 13 }}>{p.audience_strategy}</p></div>
            <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}><MapPin size={13} /> Where</div><p style={{ fontSize: 13 }}>{p.geographic_strategy}</p></div>
          </div>

          <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>How we'll measure it</div>
            <p style={{ fontSize: 13 }}>{p.measurement?.approach}</p>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>{p.measurement?.eligibility}</p>
          </div>

          {summary.advanced?.count > 0 && (
            <div className="sr-card" data-testid="easy-advanced-summary" style={{ background: "#F5F3FF", border: "1px solid #DDD6FE" }}>
              <div className="sr-eyebrow" style={{ marginBottom: 8, color: "#6D28D9" }}><Settings2 size={13} /> Advanced settings from Pro mode</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#5B21B6" }}>
                {summary.advanced.items.map((it, i) => <li key={i} style={{ marginBottom: 3 }}>{it}</li>)}
              </ul>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>These were set in Pro mode and are kept exactly as-is. <Link to="/app/growth-plan" style={{ color: "var(--blue)" }}>Switch to Pro to edit</Link> — Easy never resets them.</p>
            </div>
          )}

          <div className="sr-card" data-testid="easy-adjust" style={{ background: "#FAFBFC" }}>
            <label className="sr-label">Want a tweak? Just ask</label>
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              <input className="sr-input" value={adjust} onChange={(e) => setAdjust(e.target.value)} data-testid="easy-adjust-input" placeholder="e.g. Focus more on nearby shoppers" style={{ flex: 1, minWidth: 220 }} />
              <button className="sr-btn sr-btn-ghost" onClick={applyAdjust} disabled={busy === "adjust" || !adjust.trim()} data-testid="easy-adjust-apply">{busy === "adjust" ? <Loader2 size={15} className="sr-spin" /> : <><RefreshCw size={15} /> Adjust plan</>}</button>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <button className="sr-btn sr-btn-ghost" onClick={() => setStep(1)} data-testid="easy-step2-back"><ChevronLeft size={16} /> Budget & goal</button>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {canProposal && (
                <button className="sr-btn sr-btn-ghost" onClick={makeProposal} disabled={busy === "proposal"} data-testid="easy-create-proposal" title="Create a client proposal from this plan — does not launch or charge">
                  {busy === "proposal" ? <Loader2 size={15} className="sr-spin" /> : <><ClipboardList size={15} /> Create proposal</>}
                </button>
              )}
              <button className="sr-btn sr-btn-primary" onClick={usePlan} disabled={busy === "approve"} data-testid="easy-use-plan">{busy === "approve" ? <Loader2 size={15} className="sr-spin" /> : <>Use this plan <ChevronRight size={16} /></>}</button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3 — Design */}
      {step === 3 && plan && (
        <div data-testid="easy-step-3" style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "flex", gap: 8 }} data-testid="easy-design-mode">
            {[["ai", "AI Design", Wand2], ["upload", "Upload my own", Upload]].map(([m, lbl, Icon]) => {
              const on = designMode === m;
              return <button key={m} type="button" onClick={() => setDesignMode(m)} data-testid={`easy-design-${m}`}
                style={{ flex: 1, border: "1px solid " + (on ? "var(--navy)" : "var(--border)"), background: on ? "var(--navy)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 12, padding: "14px 16px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                <Icon size={16} style={{ marginRight: 8, verticalAlign: "-3px" }} />{lbl}</button>;
            })}
          </div>

          {designMode === "ai" && (
            <div className="sr-card">
              <p style={{ fontSize: 14 }}>We'll create on-brand ads for each channel using your saved logo, colors and offer. Preview and regenerate anything before you launch.</p>
              <button className="sr-btn sr-btn-gold" onClick={aiForAll} disabled={busy === "ai-all"} data-testid="easy-ai-generate-all" style={{ marginTop: 10 }}>
                {busy === "ai-all" ? <><Loader2 size={15} className="sr-spin" /> Designing…</> : <><Sparkles size={15} /> Generate AI creative for all channels</>}
              </button>
            </div>
          )}
          {designMode === "upload" && (
            <div className="sr-card" data-testid="easy-upload-hint">
              <p style={{ fontSize: 14 }}>Upload your own image for each channel. We check the file for you. Radio needs audio and video channels need video — fine-tune those in <Link to={`/app/growth-plan?id=${plan.id}`}>Pro mode</Link>.</p>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {planChannelKeys.map((ch) => {
              const c = (plan.creatives || {})[ch];
              const script = isScriptCh(ch);
              return (
                <div key={ch} className="sr-card" data-testid={`easy-creative-${ch}`} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ width: 84, height: 60, borderRadius: 8, overflow: "hidden", background: "#F2F4F7", display: "grid", placeItems: "center", flexShrink: 0 }}>
                    {c?.image_url ? <img src={c.image_url.startsWith("http") ? c.image_url : BACKEND + c.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon size={20} color="var(--muted)" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <strong style={{ color: "var(--navy)" }}>{CH_LABEL[ch] || ch}</strong>
                    {c ? <div style={{ fontSize: 13, marginTop: 2 }} data-testid={`easy-creative-ready-${ch}`}>{c.headline || c.title || "Creative ready"} <span className="sr-badge sr-badge-green" style={{ marginLeft: 6 }}><Check size={11} /> Ready</span></div>
                      : <div className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>{script ? "Needs an audio/video script" : "No creative yet"}</div>}
                  </div>
                  {designMode === "ai"
                    ? <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => regenChannel(ch)} disabled={busy === `regen-${ch}`} data-testid={`easy-regen-${ch}`}>{busy === `regen-${ch}` ? <Loader2 size={13} className="sr-spin" /> : <><RefreshCw size={13} /> {c ? "Regenerate" : "Generate"}</>}</button>
                    : (script
                      ? <Link to={`/app/growth-plan?id=${plan.id}`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`easy-script-pro-${ch}`}>Open in Pro</Link>
                      : <label className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`easy-upload-${ch}`} style={{ cursor: "pointer" }}>{busy === `upload-${ch}` ? <Loader2 size={13} className="sr-spin" /> : <><Upload size={13} /> Upload</>}<input type="file" accept="image/*" hidden onChange={(e) => uploadForChannel(ch, e.target.files?.[0])} /></label>)}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <button className="sr-btn sr-btn-ghost" onClick={() => setStep(2)} data-testid="easy-step3-back"><ChevronLeft size={16} /> Plan</button>
            <button className="sr-btn sr-btn-primary" onClick={() => { loadSummary(plan.id); setStep(4); }} disabled={!summary?.launch?.creative_ready} data-testid="easy-step3-continue">
              {summary?.launch?.creative_ready ? <>Review campaign <ChevronRight size={16} /></> : <>Add creative to continue</>}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 — Review */}
      {step === 4 && plan && summary && (
        <div data-testid="easy-step-4" style={{ display: "grid", gap: 14 }}>
          <div className="sr-card">
            <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Your campaign summary</div>
            <div style={{ display: "grid", gap: 10 }}>
              <Row label="Business" value={bizName} />
              <Row label="Locations" value={(summary.locations || []).map((l) => l.name).join(", ") || "All locations"} />
              <Row label="Goal" value={<span style={{ textTransform: "capitalize" }}>{plan.goal}</span>} />
              <Row label="Runs for" value={`${durationDays} days`} />
              <Row label="Channels" value={planChannelKeys.map((k) => CH_LABEL[k] || k).join(" · ")} />
              <Row label="Who we'll reach" value={p.audience_strategy} />
              <Row label="Destination" value={(summary.locations || [])[0]?.destination_url || p.offer?.headline || "Your website"} />
            </div>
          </div>

          <div className="sr-card" data-testid="easy-review-creatives">
            <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Approved creative</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {planChannelKeys.map((ch) => {
                const c = (plan.creatives || {})[ch];
                return (
                  <div key={ch} style={{ width: 120 }}>
                    <div style={{ width: 120, height: 84, borderRadius: 8, overflow: "hidden", background: "#F2F4F7", display: "grid", placeItems: "center" }}>
                      {c?.image_url ? <img src={c.image_url.startsWith("http") ? c.image_url : BACKEND + c.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon size={18} color="var(--muted)" />}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 4, color: "var(--navy)", fontWeight: 600 }}>{CH_LABEL[ch] || ch}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="sr-card" data-testid="easy-review-pricing" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)" }}>
            <div className="sr-eyebrow" style={{ marginBottom: 8 }}><DollarSign size={13} /> What you'll pay</div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}><span>Media (spent on ads)</span><strong>{usd(pr.media_budget)}/mo</strong></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginTop: 4 }}><span>Management fee ({pr.management_fee_pct}%)</span><strong>{usd(pr.management_fee)}/mo</strong></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--blue)" }}><span style={{ fontWeight: 800 }}>Total customer cost</span><strong data-testid="easy-review-total">{usd(pr.total_customer_cost)}/mo</strong></div>
          </div>

          {(summary.launch.required_approvals?.length > 0 || summary.launch.missing_items?.length > 0) && (
            <div className="sr-card" data-testid="easy-review-requirements" style={{ borderLeft: "4px solid var(--gold,#E0A800)" }}>
              <div className="sr-eyebrow" style={{ marginBottom: 6 }}><AlertTriangle size={13} /> Before launch</div>
              {summary.launch.required_approvals?.length > 0 && <p style={{ fontSize: 13 }}><strong>Approvals:</strong> {summary.launch.required_approvals.join(", ")}</p>}
              {summary.launch.missing_items?.length > 0 && <p style={{ fontSize: 13, marginTop: 4 }}><strong>Still needed:</strong> {summary.launch.missing_items.join(", ")}</p>}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <button className="sr-btn sr-btn-ghost" onClick={() => setStep(3)} data-testid="easy-step4-back"><ChevronLeft size={16} /> Design</button>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {canProposal && (
                <button className="sr-btn sr-btn-ghost" onClick={makeProposal} disabled={busy === "proposal"} data-testid="easy-create-proposal-review" title="Create a client proposal from this plan — does not launch or charge">
                  {busy === "proposal" ? <Loader2 size={15} className="sr-spin" /> : <><ClipboardList size={15} /> Create proposal</>}
                </button>
              )}
              <button className="sr-btn sr-btn-primary" onClick={() => setStep(5)} data-testid="easy-step4-continue">Continue to launch <ChevronRight size={16} /></button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 5 — Launch */}
      {step === 5 && plan && summary && (
        <div data-testid="easy-step-5" style={{ display: "grid", gap: 14 }}>
          {summary.launch.activating ? (
            <div className="sr-card" data-testid="easy-launched" style={{ padding: 28, borderTop: "4px solid var(--success,#16a34a)" }}>
              <div style={{ textAlign: "center" }}>
                <Check size={34} color="var(--success,#16a34a)" />
                <h2 style={{ fontSize: 22, marginTop: 10 }}>You're all set</h2>
                <p className="sr-muted" style={{ marginTop: 6 }}>Campaign submitted for <strong>{bizName}</strong>.</p>
                <span className={`sr-badge ${toneClass(summary.launch.overall_status)}`} data-testid="easy-launch-status" style={{ marginTop: 12, display: "inline-flex" }}><Clock size={13} /> {summary.launch.overall_status || summary.launch.post_launch_status}</span>
              </div>
              {summary.launch.campaigns?.length > 0 && (
                <div data-testid="easy-launch-tracker" style={{ marginTop: 18, display: "grid", gap: 8 }}>
                  <div className="sr-eyebrow">Your campaigns</div>
                  {summary.launch.campaigns.map((c) => (
                    <div key={c.id} data-testid={`easy-camp-${c.id}`} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
                      <div><div style={{ fontWeight: 700, fontSize: 13 }}>{c.channel}</div><div className="sr-muted" style={{ fontSize: 12 }}>{usd(c.budget_monthly)}/mo media</div></div>
                      <span className={`sr-badge ${toneClass(c.display_status)}`} data-testid={`easy-camp-status-${c.id}`}>{c.display_status}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 14, textAlign: "center" }}>Nothing shows as “Live” until operations confirms delivery — we never fake a status.</p>
              <div style={{ marginTop: 14, textAlign: "center" }}>
                <Link to="/app/campaigns" className="sr-btn sr-btn-primary" data-testid="easy-view-campaigns"><Megaphone size={16} /> View all campaigns</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="sr-card" style={{ textAlign: "center", padding: 24 }}>
                <Rocket size={28} color="var(--blue)" />
                <h2 style={{ fontSize: 22, marginTop: 10 }}>Ready to {summary.launch.launch_cta === "submit_for_review" ? "submit" : "launch"}</h2>
                <p className="sr-muted" style={{ marginTop: 6 }}>{summary.launch.launch_cta === "submit_for_review"
                  ? "Your campaign goes to our team for a quick review before anything spends."
                  : "Approve the spend below and your campaign goes live through your connected platforms."}</p>
                <div style={{ fontSize: 24, fontWeight: 800, color: "var(--navy)", marginTop: 10 }}>{usd(pr.total_customer_cost)}/mo <span style={{ fontSize: 13, fontWeight: 500, color: "var(--muted)" }}>total ({usd(pr.media_budget)} media)</span></div>
              </div>
              <label className="sr-card" data-testid="easy-spend-approve" style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input type="checkbox" checked={spendApproved} onChange={(e) => setSpendApproved(e.target.checked)} data-testid="easy-spend-checkbox" style={{ marginTop: 3 }} />
                <span style={{ fontSize: 14 }}>I approve spending <strong>{usd(pr.total_customer_cost)}/month</strong> on this campaign. No money is spent until this is approved{summary.launch.self_serve ? " and our team confirms the review" : ""}.</span>
              </label>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <button className="sr-btn sr-btn-ghost" onClick={() => setStep(4)} data-testid="easy-step5-back"><ChevronLeft size={16} /> Review</button>
                <button className="sr-btn sr-btn-gold" onClick={launch} disabled={busy === "launch" || !spendApproved} data-testid="easy-launch-btn">
                  {busy === "launch" ? <Loader2 size={15} className="sr-spin" /> : <><Rocket size={15} /> {summary.launch.launch_cta === "submit_for_review" ? "Approve & submit for review" : "Approve & launch"}</>}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: "flex", gap: 12, fontSize: 14 }}>
    <span className="sr-muted" style={{ width: 130, flexShrink: 0 }}>{label}</span>
    <span style={{ color: "var(--navy)", flex: 1 }}>{value || "—"}</span>
  </div>
);

const toneClass = (label) => ({
  "Pending Review": "sr-badge-amber", "Scheduled": "sr-badge-blue",
  "Live": "sr-badge-green", "Needs Attention": "sr-badge-red",
}[label] || "sr-badge-gray");
